<template>
    <div class="container mortgage-calculator" :class="calculatorName">

		<div class="row justify-content-center calculator-header">
			<CalculatorHeader>
				<h2>Buy to Let Mortgage Calculator</h2>
				<h4>Fill out your details and see how we can help you achieve a mortgage</h4>
			</CalculatorHeader>
		</div>
		<div class="row justify-content-center calculator-body">
			<div class="col-md-10 col-xl-8">
				<form class="calculator-container">

					<div class="mt-5" @keyup="resetCalculation" @change="resetCalculation">
						<BooleanChoiceButtons
							fieldText="Are you purchasing or remortgaging a Buy To Let"
							buttonTextLeft="Purchasing"
							buttonTextRight="Remortgaging"								
						>
							<template v-slot:left>
								<input type="radio" v-model="startOption" value="Purchasing" @click="clearRemortgagingResults">									
							</template>
							<template v-slot:right>
								<input type="radio" v-model="startOption" value="Remortgaging" @click="clearPurchasingResults">											
							</template>																
						</BooleanChoiceButtons>	

						<transition name="fade-x" mode="out-in">
							<div class="row mb-5" v-if="startOption !== ''">
								<div class="col-md-6 col-lg-4">Type of Buy to Let</div>
								<div class="col-md-6 col-lg-8 fcs-grid">
									<MultiplyChoiceButton
										buttonText="Individual"
									>
										<input type="radio" v-model="typeBTL" value="Individual" @click="clearHmoLtdResults">
									</MultiplyChoiceButton>
									<MultiplyChoiceButton
										buttonText="HMO"
									>
										<input type="radio" v-model="typeBTL" value="HMO" @click="clearIndividualLtdResults">
									</MultiplyChoiceButton>
									<MultiplyChoiceButton
										buttonText="LTD Company"
									>
										<input type="radio" v-model="typeBTL" value="LTD-Company" @click="clearIndividualHmoResults">
									</MultiplyChoiceButton>
								</div>
							</div>
						</transition>

						<transition name="fade-x" mode="out-in">
							<div v-if="typeBTL">
								<transition name="scale-in" mode="out-in"> 
									<div key="Individual" v-if="typeBTL === 'Individual'"> <!--individual section -->
										<BooleanChoiceButtons
											fieldText="Is your tax rate 40% or higher?"
											buttonTextLeft="Yes"
											buttonTextRight="No"								
										>
											<template v-slot:left>
												<input type="radio" v-model="individualTaxRate" value="Yes">									
											</template>
											<template v-slot:right>
												<input type="radio" v-model="individualTaxRate" value="No">											
											</template>																
										</BooleanChoiceButtons>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons
												v-if="typeBTL === 'Individual' && individualTaxRate"
												fieldText="If necessary, would you be prepared to fix your mortgage for 5 years to potentially increase your borrowing?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="individualFixedMortgage" value="Yes">						
												</template>
												<template v-slot:right>
													<input type="radio" v-model="individualFixedMortgage" value="No">								
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<InputElement
												v-if="typeBTL === 'Individual' && individualFixedMortgage"
												fieldText="How much rent do you get or expect to get?"
											>
												<InputTypeNumber currencyPlaceholder v-model="individualExpectedRent" @keyup="resetCalculation"/>
											</InputElement>
										</transition>														
									</div>

									<div key="HMO" v-if="typeBTL === 'HMO'"> <!--HMO section -->
										<BooleanChoiceButtons
											fieldText="Will or is the property held in a limited company?"
											buttonTextLeft="Yes"
											buttonTextRight="No"								
										>
											<template v-slot:left>
												<input type="radio" v-model="hmoPropertyHeldIn" value="Yes">									
											</template>
											<template v-slot:right>
												<input type="radio" v-model="hmoPropertyHeldIn" value="No">											
											</template>																
										</BooleanChoiceButtons>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons
												v-if="typeBTL === 'HMO' && hmoPropertyHeldIn"
												fieldText="Does the property have an HMO Licence?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="hmoLicence" value="Yes">				
												</template>
												<template v-slot:right>
													<input type="radio" v-model="hmoLicence" value="No">					
												</template>																
											</BooleanChoiceButtons>
										</transition>										

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons
												v-if="typeBTL === 'HMO' && hmoPropertyHeldIn && hmoLicence"
												fieldText="If necessary, would you be prepared to fix your mortgage for 5 years to potentially increase your borrowing?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="hmoFixedMortgage" value="Yes">						
												</template>
												<template v-slot:right>
													<input type="radio" v-model="hmoFixedMortgage" value="No">								
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<InputElement
												v-if="typeBTL === 'HMO' && hmoPropertyHeldIn && hmoLicence && hmoFixedMortgage"
												fieldText="How much rent do you get or expect to get?"
											>
												<InputTypeNumber currencyPlaceholder v-model="hmoExpectedRent" @keyup="resetCalculation"/>
											</InputElement>
										</transition>																						
									</div>

									<div key="LTD-company"  v-if="typeBTL === 'LTD-Company'"> <!--LTD section -->
										<BooleanChoiceButtons
											fieldText="Do you have an SPV in place?"
											buttonTextLeft="Yes"
											buttonTextRight="No"								
										>
											<template v-slot:left>
												<input type="radio" v-model="ltdSpvInPlace" value="Yes">									
											</template>
											<template v-slot:right>
												<input type="radio" v-model="ltdSpvInPlace" value="No">										
											</template>																
										</BooleanChoiceButtons>

										<transition name="fade-x" mode="out-in">
											<InputElement
												v-if="typeBTL === 'LTD-Company' && ltdSpvInPlace"
												fieldText="How many Directors are in the company?"
											>
												<InputTypeNumber v-model="ltdDirectorlQuantity" />
											</InputElement>
										</transition>

										<transition name="fade-x" mode="out-in">
											<InputElement
												v-if="typeBTL === 'LTD-Company' && ltdSpvInPlace && ltdDirectorlQuantity"
												fieldText="How much personal income do you earn per year?"
											>
												<InputTypeNumber v-model="ltdDirectorlIncome" />
											</InputElement>
										</transition>

										<transition name="fade-x" mode="out-in">
											<BooleanChoiceButtons
												v-if="typeBTL === 'LTD-Company' && ltdSpvInPlace && ltdDirectorlQuantity && ltdDirectorlIncome"
												fieldText="If necessary, would you be prepared to fix your mortgage for 5 years to potentially increase your borrowing?"
												buttonTextLeft="Yes"
												buttonTextRight="No"								
											>
												<template v-slot:left>
													<input type="radio" v-model="ltdFixedMortgage" value="Yes">						
												</template>
												<template v-slot:right>
													<input type="radio" v-model="ltdFixedMortgage" value="No">								
												</template>																
											</BooleanChoiceButtons>
										</transition>

										<transition name="fade-x" mode="out-in">
											<InputElement
												v-if="typeBTL === 'LTD-Company' && ltdSpvInPlace && ltdDirectorlQuantity && ltdDirectorlIncome && ltdFixedMortgage"
												fieldText="How much rent do you get or expect to get?"
											>
												<InputTypeNumber currencyPlaceholder v-model="ltdExpectedRent" @keyup="resetCalculation" />
											</InputElement>
										</transition>

									</div>																	
								</transition>
							</div>											
						</transition>

						<transition name="fade-x" mode="out-in">
							<div v-if="sectionCompleted && startOption === 'Purchasing'">
								<BooleanChoiceButtons							
									fieldText="Do you have a property in mind?"
									buttonTextLeft="Yes"
									buttonTextRight="No"								
								>
									<template v-slot:left>
										<input type="radio" v-model="propertyInMind" value="Yes" @click="clearApproxPurchaseValue">							
									</template>
									<template v-slot:right>
										<input type="radio" v-model="propertyInMind" value="No" @click="clearPurchaseValue">									
									</template>																
								</BooleanChoiceButtons>

								<transition name="fade-x" mode="out-in">
									<div v-if="startOption === 'Purchasing' && propertyInMind !== ''">
										<transition name="scale-in" mode="out-in">
											<div key="Yes" v-if="startOption === 'Purchasing' && propertyInMind === 'Yes'">
												<InputElement						
													fieldText="What is the purchase price?"
												>
													<InputTypeNumber currencyPlaceholder v-model="purchaseValue" />
												</InputElement>
											</div>
											<div key="No" v-if="startOption === 'Purchasing' && propertyInMind === 'No'">
												<InputElement									
													fieldText="Approximately, what value would you like to purchase at?"
												>
													<InputTypeNumber currencyPlaceholder v-model="approxPurchaseValue" />
												</InputElement>
											</div>
										</transition>
									</div>
								</transition>

								<transition name="fade-x" mode="out-in">
									<div v-if="startOption === 'Purchasing' && propertyInMind !== '' && (purchaseValue > 0 || approxPurchaseValue > 0) && typeBTL === 'HMO'">
										<InputElement
											fieldText="How many lettable rooms does the property have?"
										>
											<InputTypeNumber v-model="hmoLettableRooms" />
										</InputElement>
									</div>								
								</transition>

								<transition name="fade-x" mode="out-in">
									<InputElement
										v-if="startOption === 'Purchasing' && propertyInMind && (purchaseValue > 0 || approxPurchaseValue > 0)"							
										fieldText="What is your required mortgage amount?"
									>
										<InputTypeNumber currencyPlaceholder v-model="requiredMortgageAmount" />
									</InputElement>
								</transition>

								<transition name="fade-x" mode="out-in">
									<BooleanChoiceButtons
										v-if="startOption === 'Purchasing' && propertyInMind && requiredMortgageAmount > 0"
										fieldText="Are you a first time landlord?"
										buttonTextLeft="Yes"
										buttonTextRight="No"								
									>
										<template v-slot:left>
											<input type="radio" v-model="firstTimeLandlord" value="Yes" @click="clearPropertiesQuantity">						
										</template>
										<template v-slot:right>
											<input type="radio" v-model="firstTimeLandlord" value="No">									
										</template>																
									</BooleanChoiceButtons>
								</transition>

								<transition name="fade-x" mode="out-in">
									<InputElement
										v-if="startOption === 'Purchasing' && propertyInMind && requiredMortgageAmount > 0 && firstTimeLandlord === 'No'"
										fieldText="How many rental properties do you own?"
									>
										<InputTypeNumber v-model="propertiesQuantity" />
									</InputElement>
								</transition>																																														
							</div>
						</transition>

						<transition name="fade-x" mode="out-in">
							<div v-if="sectionCompleted && startOption === 'Remortgaging'">
								<InputElement
									fieldText="What is the approximate value of your property?"
								>
									<InputTypeNumber currencyPlaceholder v-model="approxPropValue"/>
								</InputElement>

								<transition name="fade-x" mode="out-in">
									<InputElement
										v-if="startOption === 'Remortgaging' && approxPropValue > 0"
										fieldText="What is your current outstanding mortgage amount?"
									>
										<InputTypeNumber currencyPlaceholder v-model="outstandingMortgage" />
									</InputElement>
								</transition>

								<transition name="fade-x" mode="out-in">
									<InputElement
										v-if="startOption === 'Remortgaging' && approxPropValue > 0 && outstandingMortgage > 0"							
										fieldText="Who is your current lender?"
									>
										<input class="form-control calc-form-control" type="text" v-model="currentLender">
									</InputElement>
								</transition>

								<transition name="fade-x" mode="out-in">
									<BooleanChoiceButtons
										v-if="startOption === 'Remortgaging' && approxPropValue > 0 && outstandingMortgage > 0 && currentLender"
										fieldText="Are you remortgaging with additional borrowing?"
										buttonTextLeft="Yes"
										buttonTextRight="No"								
									>
										<template v-slot:left>
											<input type="radio" v-model="additionalBorrowing" value="Yes">								
										</template>
										<template v-slot:right>
											<input type="radio" v-model="additionalBorrowing" value="No">										
										</template>																
									</BooleanChoiceButtons>
								</transition>

								<transition name="fade-x" mode="out-in">
									<InputElement
										v-if="startOption === 'Remortgaging' && approxPropValue > 0 && outstandingMortgage > 0 && currentLender && additionalBorrowing === 'Yes'"
										fieldText="How much more would you like to borrow?"
									>
										<InputTypeNumber currencyPlaceholder v-model="borrowAmount" />
									</InputElement>
								</transition>

								<transition name="fade-x" mode="out-in">
									<BooleanChoiceButtons
										v-if="startOption === 'Remortgaging' && approxPropValue > 0 && outstandingMortgage > 0 && currentLender && additionalBorrowing"
										fieldText="Do you have any more rental properties?"
										buttonTextLeft="Yes"
										buttonTextRight="No"								
									>
										<template v-slot:left>
											<input type="radio" v-model="additionalRentalProps" value="Yes">								
										</template>
										<template v-slot:right>
											<input type="radio" v-model="additionalRentalProps" value="No" @click="clearAdditionalRentalPropsAmount">										
										</template>																
									</BooleanChoiceButtons>						
								</transition>							

								<transition name="fade-x" mode="out-in">
									<InputElement
										v-if="startOption === 'Remortgaging' && approxPropValue > 0 && outstandingMortgage > 0 && currentLender && additionalBorrowing && additionalRentalProps === 'Yes'"							
										fieldText="Do you have any more rental properties?"
									>
										<InputTypeNumber v-model="additionalRentalPropsAmount" />
									</InputElement>
								</transition>
							</div>
						</transition>

						<transition name="scale-in" mode="out-in">
							<div
								v-if="showCalculateButton" 
								class="text-center mt-5 mb-3"
							>
								<div class="btn calc-btn-secondary" @click="calculate">CALCULATE</div>
							</div>
						</transition>
					</div>

					<transition name="fade-y" mode="out-in">
						<div v-if="calcCompleted">
							
							<ResultsContainer class="mt-5 mb-5 calc-results-box">
								<div class="mb-3 results-title">You may be able to borrow:</div>
								<span class="fcs-price">£{{this.result}}</span>
								<div class="mt-3">The figure quoted gives you an indication based upon the likely rent being achieved, the actual borrowing may be limited to 80% of the price or value of property.</div>
							</ResultsContainer>

							<SubmissionForm :calculatorName="calculatorName" :calculatorData="collateMessageBody" />

						</div>
					</transition>
				</form>
			</div>
		</div>

    </div>
</template>

<script>
import BooleanChoiceButtons from '@/components/utilities/booleanChoiceButtons.vue'
import CalculatorHeader from '@/components/utilities/calculatorHeader.vue'
import InputElement from '@/components/utilities/inputElement.vue'
import InputTypeNumber from '@/components/utilities/inputTypeNumber.vue'
import MultiplyChoiceButton from '@/components/utilities/multiplyChoiceButton.vue'
import ResultsContainer from '@/components/utilities/resultsContainer.vue'
import SubmissionForm from '@/components/utilities/submissionForm.vue'

export default {
	components: {
		BooleanChoiceButtons,
		CalculatorHeader,
		InputElement,
		InputTypeNumber,
		MultiplyChoiceButton,
		ResultsContainer,
		SubmissionForm
	},	
    data() {
        return {
			calculatorName: 'buy-to-let-calculator',

			startOption: '',		
			typeBTL: '',

			//Individual
			individualTaxRate: '',
			individualFixedMortgage: 0,
			individualExpectedRent: 0,

			//HMO
			hmoPropertyHeldIn: '',
			hmoLicence: '',
			hmoLettableRooms: 0,
			hmoFixedMortgage: 0,
			hmoExpectedRent: 0,

			//LTD
			ltdSpvInPlace: '',
			ltdDirectorlQuantity: 0,
			ltdDirectorlIncome: 0,			
			ltdFixedMortgage: '',
			ltdExpectedRent: 0,

			//Purchasing
			propertyInMind: '',
			purchaseValue: 0,
			approxPurchaseValue: 0,
			requiredMortgageAmount: 0,
			firstTimeLandlord: '',
			propertiesQuantity: 0,			

			//Remortgaging
			approxPropValue: 0,
			outstandingMortgage: 0,
			currentLender: '',
			additionalBorrowing: '',
			borrowAmount: 0,
			additionalRentalProps: '',
			additionalRentalPropsAmount: 0,				

			result: '',
			calcCompleted: false
        }
    },
    methods: {
        calculate() {
			switch (this.typeBTL) {
				case "Individual":
					this.individualTaxRate === 'Yes'
						? this.result = this.indiv40Calc.toFixed(2)
						: this.result = this.indiv20Calc.toFixed(2)
					break;
				case "HMO":
					if( this.hmoExpectedRent > 0 ) { this.result = this.hmoCalc.toFixed(2) }
					break;
				case "LTD-Company":
					if( this.ltdExpectedRent > 0 ) { this.result = this.ltdCalc.toFixed(2) }
					break;					
				default:
					this.result = ''
					break;
			}
			this.calcCompleted = true;			
        },
        removeHyphensToUpperCase(str) {
            return str.replace(/(-|^)([^-]?)/g, function(_, prep, letter) { return (prep && ' ') + letter.toUpperCase(); });
		},
		clearPurchasingResults() {
			this.propertyInMind = '',
			this.purchaseValue = 0,
			this.approxPurchaseValue = 0,
			this.requiredMortgageAmount = 0,
			this.firstTimeLandlord = '',
			this.propertiesQuantity = 0			
		},
		clearRemortgagingResults() {
			this.approxPropValue = 0,
			this.outstandingMortgage = 0,
			this.currentLender = '',
			this.additionalBorrowing = '',
			this.borrowAmount = 0,
			this.additionalRentalProps = '',
			this.additionalRentalPropsAmount = 0				
		},
		clearHmoLtdResults() {
			this.hmoPropertyHeldIn = '',
			this.hmoLicence = '',
			this.hmoLettableRooms = 0,
			this.hmoFixedMortgage = 0,
			this.hmoExpectedRent = 0			
			this.ltdSpvInPlace = '',
			this.ltdDirectorlQuantity = 0,
			this.ltdDirectorlIncome = 0,			
			this.ltdFixedMortgage = '',
			this.ltdExpectedRent = 0
		},
		clearIndividualLtdResults() {
			this.individualTaxRate = '',
			this.individualFixedMortgage = 0,
			this.individualExpectedRent = 0,
			this.ltdSpvInPlace = '',
			this.ltdDirectorlQuantity = 0,
			this.ltdDirectorlIncome = 0,			
			this.ltdFixedMortgage = '',
			this.ltdExpectedRent = 0						
		},
		clearIndividualHmoResults() {
			this.individualTaxRate = '',
			this.individualFixedMortgage = 0,
			this.individualExpectedRent = 0,
			this.hmoPropertyHeldIn = '',
			this.hmoLicence = '',
			this.hmoLettableRooms = 0,
			this.hmoFixedMortgage = 0,
			this.hmoExpectedRent = 0
		},
		clearApproxPurchaseValue() {
			this.approxPurchaseValue = 0
		},
		clearPurchaseValue() {
			this.purchaseValue = 0
		},
		clearPropertiesQuantity() {
			this.propertiesQuantity = 0
		},
		clearAdditionalRentalPropsAmount() {
			this.additionalRentalPropsAmount = 0
		},
		resetCalculation() {
			this.calcCompleted = false;
		},
		formatEmptyMessageFields(message){
			return message ? message : 'N/A'
		}					
    },	
    computed: {
		/**
		 * Check if previous logical section is completed
		 * @return {Bool}
		 */
        sectionCompleted() {
			return ( this.individualExpectedRent || this.hmoExpectedRent || this.ltdExpectedRent ) ? true : false
		},
		/**
		 * Show button if previous section is completed
		 * @return {Bool}
		 */		
		showCalculateButton() {
			let calculateButton
			this.sectionCompleted
				? ( this.firstTimeLandlord === 'Yes' || this.additionalRentalProps === 'No' ) || ( this.propertiesQuantity >= 0 || this.additionalRentalPropsAmount > 0 ) ? calculateButton = true : calculateButton = false
				: calculateButton = false
			return calculateButton			
		},				
        indiv20Calc() {
			return (((this.individualExpectedRent * 12) / 1.25) / 0.05);
		},
		indiv40Calc() {
			return (((this.individualExpectedRent * 12) / 1.40) / 0.0525);
		},
		hmoCalc() {
			return (((this.hmoExpectedRent * 12) / 1.70) / 0.065);
		},
		ltdCalc() {
			return (((this.ltdExpectedRent * 12) / 1.25) / 0.05);
		},
		collateMessageBody() {
			const calculatorData = {
				calculatorTitle: this.removeHyphensToUpperCase(this.calculatorName),
				calculatorResults:
				`
					Are you purchasing or remortgaging a Buy To Let: ${this.startOption}
					Type of Buy to Let: ${this.typeBTL}
					
					Individual:
						Is your tax rate 40% or higher? ${this.formatEmptyMessageFields(this.individualTaxRate)}
						If necessary, would you be prepared to fix your mortgage for 5 years to potentially increase your borrowing? £${this.formatEmptyMessageFields(this.individualFixedMortgage)}
						How much rent do you get or expect to get? £${this.formatEmptyMessageFields(this.individualExpectedRent)}
					HMO:
						Will or is the property held in a limited company? ${this.formatEmptyMessageFields(this.hmoPropertyHeldIn)}
						Does the property have an HMO Licence? ${this.formatEmptyMessageFields(this.hmoLicence)}
						How many lettable rooms does the property have? ${this.formatEmptyMessageFields(this.hmoLettableRooms)}
						If necessary, would you be prepared to fix your mortgage for 5 years to potentially increase your borrowing? £${this.formatEmptyMessageFields(this.hmoFixedMortgage)}
						How much rent do you get or expect to get? £${this.formatEmptyMessageFields(this.hmoExpectedRent)}
					LTD-Company:
						Do you have an SPV in place? ${this.formatEmptyMessageFields(this.ltdSpvInPlace)}
						How many Directors are in the company? ${this.formatEmptyMessageFields(this.ltdDirectorlQuantity)}
						How much personal income do you earn per year? £${this.formatEmptyMessageFields(this.ltdDirectorlIncome)}
						If necessary, would you be prepared to fix your mortgage for 5 years to potentially increase your borrowing? £${this.formatEmptyMessageFields(this.ltdFixedMortgage)}
						How much rent do you get or expect to get? £${this.formatEmptyMessageFields(this.ltdExpectedRent)}

					Do you have a property in mind? ${this.propertyInMind}
					
					What is the purchase price? £${this.formatEmptyMessageFields(this.purchaseValue)}
					Approximately, what value would you like to purchase at? £${this.formatEmptyMessageFields(this.approxPurchaseValue)}				
					What is your required mortgage amount? £${this.requiredMortgageAmount}
					Are you a first time landlord? ${this.firstTimeLandlord}				
					How many rental properties do you own? ${this.propertiesQuantity}
					
					What is the approximate value of your property? £${this.approxPropValue}
					What is your current outstanding mortgage amount? £${this.outstandingMortgage}
					Who is your current lender? ${this.currentLender}
					Are you remortgaging with additional borrowing? ${this.additionalBorrowing}			
					How much more would you like to borrow? £${this.borrowAmount}
					Do you have any more rental properties? ${this.additionalRentalProps}				
					How many? ${this.additionalRentalPropsAmount}
					
					You may be able to borrow: £${this.result}
				`
			}
			
			return calculatorData;
		}		
	}
}
</script>

<style lang="scss">
	@import './styles/calcs.scss';
</style>